import React, { useState } from 'react';
import { CheckCircle2, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { CONDITIONS, CONTACT_METHODS } from '../data/enquiry';
import { PRACTICE, WHATSAPP_LINK } from '../data/practice';

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = 'mt-1 w-full rounded-lg border border-sky-gray/40 bg-white px-3 py-2.5 text-rabbit placeholder:text-chocolate-malt/50 focus:outline-none focus:ring-2 focus:ring-burnt-coral/60 focus:border-burnt-coral';

const Field = ({ label, htmlFor, children, hint }) => (
  <label htmlFor={htmlFor} className="block text-sm font-medium text-rabbit">
    {label}
    {children}
    {hint && <span className="block text-xs font-normal text-chocolate-malt/70 mt-1">{hint}</span>}
  </label>
);

const Success = ({ reference, method }) => (
  <div className="rounded-2xl border border-[#01A9B4]/30 bg-[#01A9B4]/5 p-8 text-center" role="status" data-testid="enquiry-success">
    <CheckCircle2 className="w-12 h-12 text-[#006A71] mx-auto mb-4" aria-hidden="true" />
    <h3 className="text-xl font-bold text-rabbit mb-2">Thank you — we've received your enquiry</h3>
    <p className="text-chocolate-malt">Your reference is <strong data-testid="enquiry-reference">{reference}</strong>. We'll be in touch by {method} within one working day.</p>
    <p className="text-sm text-chocolate-malt/80 mt-4">Need us sooner? Call <a href={PRACTICE.phoneTel} className="underline underline-offset-4">{PRACTICE.phoneDisplay}</a> or <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">WhatsApp us</a>.</p>
  </div>
);

/**
 * Enquiry form → POST /api/contact. `defaultCondition` pre-selects the dropdown; `source` records the originating page.
 */
const EnquiryForm = ({ defaultCondition = '', defaultMessage = '', source = '', compact = false }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', preferred_contact: 'email', condition: defaultCondition, message: defaultMessage });
  const [status, setStatus] = useState({ busy: false, error: '', ref: '' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.condition) return setStatus({ busy: false, error: 'Please choose the treatment or condition you are interested in.', ref: '' });
    setStatus({ busy: true, error: '', ref: '' });
    try {
      const res = await fetch(`${API}/api/contact`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, source: source || window.location.pathname }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Please check your details and try again.');
      setStatus({ busy: false, error: '', ref: data.referenceId });
    } catch (err) {
      setStatus({ busy: false, error: err.message, ref: '' });
    }
  };

  if (status.ref) return <Success reference={status.ref} method={CONTACT_METHODS.find((m) => m.value === form.preferred_contact)?.label.toLowerCase()} />;

  return (
    <form onSubmit={submit} className={`space-y-5 ${compact ? '' : 'rounded-2xl border border-sky-gray/20 bg-white p-6 md:p-8 shadow-sm'}`} data-testid="enquiry-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Full Name *" htmlFor="enq-name">
          <input id="enq-name" required minLength={2} autoComplete="name" value={form.name} onChange={set('name')} placeholder="Jane Smith" className={inputCls} data-testid="enquiry-name" />
        </Field>
        <Field label="Email Address *" htmlFor="enq-email">
          <input id="enq-email" type="email" required autoComplete="email" value={form.email} onChange={set('email')} placeholder="jane@example.com" className={inputCls} data-testid="enquiry-email" />
        </Field>
        <Field label="Phone Number *" htmlFor="enq-phone">
          <input id="enq-phone" type="tel" required minLength={7} autoComplete="tel" value={form.phone} onChange={set('phone')} placeholder="07700 900123" className={inputCls} data-testid="enquiry-phone" />
        </Field>
        <Field label="Preferred Contact Method *" htmlFor="enq-method">
          <select id="enq-method" required value={form.preferred_contact} onChange={set('preferred_contact')} className={inputCls} data-testid="enquiry-method">
            {CONTACT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Condition of Interest *" htmlFor="enq-condition">
        <select id="enq-condition" required value={form.condition} onChange={set('condition')} className={inputCls} data-testid="enquiry-condition">
          <option value="" disabled>Select a treatment or condition…</option>
          {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Tell us a bit more (optional)" htmlFor="enq-message" hint="e.g. what's bothering you, how long for, and any times that suit you.">
        <textarea id="enq-message" rows={4} maxLength={2000} value={form.message} onChange={set('message')} className={inputCls} data-testid="enquiry-message" />
      </Field>
      {status.error && <p className="text-sm text-red-600" role="alert" data-testid="enquiry-error">{status.error}</p>}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Button type="submit" size="lg" disabled={status.busy} className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" data-testid="enquiry-submit">
          {status.busy ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" aria-hidden="true" />Sending…</> : <><Send className="w-5 h-5 mr-2" aria-hidden="true" />Send enquiry</>}
        </Button>
        <p className="text-xs text-chocolate-malt/70">We reply within one working day. Your details are used only to respond to this enquiry.</p>
      </div>
    </form>
  );
};

export default EnquiryForm;
